import { z } from 'zod';

import { PrimaryKeySchema, commaDelimitedList } from './utils';

/**
 * Schema for agent runs which store the input, output, and metadata for agent executions
 */
export const AgentRunSchema = z.object({
  agentRunId: PrimaryKeySchema,
  agentInput: z.record(z.any()),
  agentInputSchema: z.record(z.any()),
  agentInputSummary: z.string(),
  agentOutput: z.record(z.any()).nullable(),
  agentOutputSchema: z.record(z.any()).nullable(),
  agentOutputSummary: z.string().nullable(),
  agentType: z.enum(['general', 'task', 'search', 'conversation']),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  maxActions: z.number().nullable(),
  planningInterval: z.number().nullable(),
  currentStep: z.number().nullable(),
  thoughts: z.array(
    z.object({
      text: z.string(),
      createdAt: z.coerce.date(),
    }),
  ),
  startedAt: z.coerce.date().nullable(),
  cancelledAt: z.coerce.date().nullable(),
  succeededAt: z.coerce.date().nullable(),
  failedAt: z.coerce.date().nullable(),
});

export type AgentRun = z.infer<typeof AgentRunSchema>;

export const AgentRunsSchema = z.array(AgentRunSchema);

export type AgentRuns = z.infer<typeof AgentRunsSchema>;

export const CreateAgentRunSchema = AgentRunSchema.pick({
  agentInput: true,
  agentInputSchema: true,
  agentType: true,
});

export type CreateAgentRun = z.infer<typeof CreateAgentRunSchema>;

export const UpdateAgentRunSchema = z.object({
  shouldStart: z.boolean().optional(),
  shouldStop: z.boolean().optional(),
});

export type UpdateAgentRun = z.infer<typeof UpdateAgentRunSchema>;

export const AgentRunsQueryOptionsSchema = z.object({
  agentRunIds: commaDelimitedList(z.string().array()).optional(),
  taskIds: commaDelimitedList(z.string().array()).optional(),
  conversationIds: commaDelimitedList(z.string().array()).optional(),
  agentTypes: commaDelimitedList(
    z.enum(['general', 'task', 'search', 'conversation']).array(),
  ).optional(),
});

export type AgentRunsQueryOptions = z.infer<typeof AgentRunsQueryOptionsSchema>;
